/**
 * CriteriaSection
 *
 * Renders the Criteria Evaluation section inside the TradeDrawer.
 * Displays a checklist of criteria snapshots with pass/fail toggle via checkbox.
 * Shows an "X/Y met" summary badge above the list.
 *
 * @see CriteriaSnapshot — criteria data shape
 */

// 1. External dependencies
import React, { useMemo } from 'react';

// 2. Internal absolute imports
import { Checkbox } from '@enfomentis/ui';

// 3. Type imports
import type { CriteriaSnapshot } from '@/types/shared/StrategySnapshots';

// ===== TYPES =====

interface CriteriaSectionProps {
  criteria: CriteriaSnapshot[];
  onChange: (updated: CriteriaSnapshot[]) => void;
}

// ===== COMPONENT =====

export const CriteriaSection: React.FC<CriteriaSectionProps> = ({ criteria, onChange }) => {
  const metCount = useMemo(() => criteria.filter((c) => c.checked).length, [criteria]);

  const toggle = (criteriaId: string): void => {
    onChange(criteria.map((c) => c.criteriaId === criteriaId ? { ...c, checked: !c.checked } : c));
  };

  if (criteria.length === 0) {
    return (
      <p className="text-xs text-text-tertiary italic">Select a playbook to populate criteria.</p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* X/Y met badge */}
      <span className="text-xs text-text-secondary">
        <span className={`font-semibold ${metCount === criteria.length ? 'text-success' : 'text-text-primary'}`}>
          {metCount}/{criteria.length}
        </span>
        {' '}met
      </span>

      {/* Criteria list */}
      <div className="flex flex-col gap-1">
        {criteria.map((c) => (
          <Checkbox
            key={c.criteriaId}
            isSelected={c.checked}
            onValueChange={() => toggle(c.criteriaId)}
            size="sm"
            classNames={{ label: 'text-xs text-text-primary' }}
          >
            {c.name}
          </Checkbox>
        ))}
      </div>
    </div>
  );
};
